import type { FoodAndDrinkSectionType } from "@/lib/requests/types";
import { cn } from "@/lib/utils/cn";
import { LucideMapPinned, type LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { useLocationContext } from "./LocationContext";
import { SimpleSectionTitle } from "./SimpleSectionTitle";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { generateGoogleMapLink } from "@/lib/utils";

export const FamousFood = ({
  foodAndDrink,
  destination,
  id,
}: {
  foodAndDrink: FoodAndDrinkSectionType;
  destination: string;
  id: string;
}) => (
  <CardSection
    id={id}
    title={`${destination} Famous Food`}
    subtitle={`Signature dishes, delicacies and famous food`}
    cards={foodAndDrink.localDishes.map((props, index) => ({
      ...props,
      ...(index === 0 ? { badge: "Must-Try!" } : {}),
    }))}
  />
);

export const RestaurantsAndCafes = ({
  foodAndDrink,
  destination,
}: {
  foodAndDrink: FoodAndDrinkSectionType;
  destination: string;
}) => (
  <CardSection
    title="Restaurants & Cafés"
    subtitle={`Best places to eat and drink in ${destination}`}
    cards={foodAndDrink.restaurantsAndCafes}
  />
);

// export const RestaurantsAndCafes = ({
//   foodAndDrink,
// }: {
//   foodAndDrink: FoodAndDrinkSectionType;
// }) => (
//   <CardSection
//     title="Restaurants & Cafés"
//     subtitle="Suggestions on restaurants and cafes "
//     cards={foodAndDrink.restaurantsAndCafes}
//     showsMapLink={true}
//   />
// );

export const FoodMarketsAndStreetFood = ({
  foodAndDrink,
}: {
  foodAndDrink: FoodAndDrinkSectionType;
}) => (
  <CardSection
    title="Food Markets & Street Food"
    subtitle="A food market and street food guide"
    cards={foodAndDrink.foodMarketsAndStreetFood}
    showsMapLink={true}
  />
);

export const DietaryRestrictions = ({
  foodAndDrink,
}: {
  foodAndDrink: FoodAndDrinkSectionType;
}) => (
  <CardSection
    title="Dietary Restrictions"
    subtitle="Navigating dietary restrictions locally"
    cards={foodAndDrink.dietaryRestrictions.map((props) => ({
      ...props,
      badge: props.name
        .split(" ")
        .filter((word) =>
          ["vegetarian", "vegan", "gluten-free"].includes(word.toLowerCase()),
        )
        .join(", "),
    }))}
    showsMapLink={false}
  />
);

export const CardSection = ({
  title,
  subtitle,
  cards,
  showsMapLink = false,
  id,
  noH2,
}: {
  title: string;
  subtitle: string;
  cards: ComponentProps<typeof CardSectionCard>[];
  showsMapLink?: boolean;
  id?: string;
  noH2?: boolean;
}) => (
  <section className="w-full relative mt-20 md:mt-28 lg:mt-32 xl:mt-48 2xl:mt-44">
    <div id={id} className="absolute -top-28 md:-top-32 " />
    <div className="container px-4 md:px-6">
      <SimpleSectionTitle title={title} subtitle={subtitle} noH2={noH2} />
      <div
        className={cn(
          "grid gap-8 mt-16 mx-auto",
          cards.length === 1 && "max-w-screen-sm",
          cards.length === 2 && "sm:grid-cols-2 max-w-screen-lg",
          cards.length === 3 && "lg:grid-cols-3 max-w-screen-xl",
          cards.length === 4 && "sm:grid-cols-2 max-w-screen-lg",
          cards.length > 4 && "sm:grid-cols-3 max-w-screen-xl",
        )}
      >
        {cards.map((props) => (
          <CardSectionCard
            key={props.name}
            {...props}
            showsMapLink={showsMapLink}
          />
        ))}
      </div>
    </div>
  </section>
);

export const TextSection = ({
  title,
  text,
  id,
  className,
  theme,
  Icon,
  noH2,
}: {
  title: string;
  text: React.ReactNode;
  id?: string;
  className?: string;
  theme?: "dark" | "orange" | "border" | "blue" | "green" | "yellow" | "red";
  Icon: LucideIcon;
  noH2?: boolean;
}) => (
  <div
    className={cn(
      "p-12 md:p-20 lg:p-24 px-3.5 mt-20 md:mt-28 lg:mt-32 xl:mt-36 2xl:mt-44 ",
      theme === "dark" && "bg-foreground",
      theme === "blue" && "bg-blue-300/90",
      theme === "orange" && "bg-orange-300/80",
      theme === "yellow" && "bg-yellow-400/60",
      theme === "border" && "bg-border",
      theme === "green" && "bg-emerald-400/70",
      theme === "red" && "bg-red-300/80",
      className,
    )}
  >
    <Icon
      className={cn(
        "w-10 h-10 sm:w-14 sm:h-14 mx-3.5 sm:mx-auto",
        theme === "dark" && "text-yellow-300",
        theme === "blue" && "text-blue-900",
        theme === "orange" && "text-orange-900",
        theme === "border" && "text-yellow-700",
      )}
    />
    <div className="relative flex flex-col sm:flex-row gap-16 mt-8 sm:mt-12 mb-24 sm:mb-28 rounded max-w-screen-xl mx-auto px-4">
      <div id={id} className="absolute -top-44 sm:-top-48 2xl:-top-64" />
      <div className="grid lg:grid-cols-2 gap-6 md:gap-8 w-full">
        <div
          className={cn(
            "text-4xl font-medium tracking-tighter sm:text-5xl md:text-6xl leading-[1.2] sm:leading-[1.2] md:leading-[1.2]",
            theme === "dark" && "text-background",
          )}
        >
          {noH2 ? <p>{title}</p> : <h2>{title}</h2>}
        </div>
        <div className="font-light text-[17px] md:text-lg leading-relaxed md:leading-relaxed mt-2 mb-14">
          {text}
        </div>
      </div>
    </div>
  </div>
);

const CardSectionCard = ({
  name,
  description,
  showsMapLink = false,
  badge,
}: {
  name: string;
  description: string;
  showsMapLink?: boolean;
  badge?: React.ReactNode;
}) => {
  const { lat, lng } = useLocationContext();
  return (
    <Card className="relative group p-8">
      <span className="font-medium text-xl md:text-2xl leading-relaxed first-letter:capitalize">
        {name}
      </span>
      <p className="font-light text-[17px] md:text-lg leading-normal md:leading-normal mt-2 mb-14 text-foreground/70">
        {description}
      </p>
      {showsMapLink && (
        <div className="flex items-center justify-end absolute bottom-4 right-4">
          <Button size="sm" variant="secondary" asChild>
            <a
              className="flex gap-2 items-center"
              href={generateGoogleMapLink({
                lat,
                lng,
                location: name,
                zoom: "16",
              })}
              target="_blank"
              rel="noopener nofollow"
            >
              <p>View</p>
              <LucideMapPinned size={20} />
            </a>
          </Button>
        </div>
      )}
      {badge && (
        <p className="absolute -top-4 -right-3.5 sm:-right-6 bg-green-400 rounded-full text-green-900 px-3.5 py-1.5">
          {badge}
        </p>
      )}
    </Card>
  );
};
